import React, { useState, useEffect, useRef } from "react";
import { Modal, Form, Input, Button, message, Select, Space } from "antd";
import {
  DeleteOutlined,
  IdcardOutlined,
  MailOutlined,
  PlusOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { VALIDATION_RULES } from "../../../constants/Constants";
import { API_BASE_URL } from "../../../services/apiService";
import axios from "axios";

const { Option } = Select;

const InviteForm = ({ isVisible, onClose, supplierId }) => {
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  const validateEmailAsync = async (_, value) => {
    if (!value) return Promise.resolve();

    try {
      const response = await axios.get(
        `https://techsolutionsservice.ustdev.com/signup/emailCheck/${value}`
        ,{
          headers:{
            Authorization:`Bearer ${sessionStorage.getItem("authToken")}`
          }
        }
      );
      if (response.data) {
        return Promise.reject(new Error(response.data));
      }
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(
        new Error(error.response?.data || "Error validating email")
      );
    }
  };

  const validateUserIdAsync = async (_, value) => {
    if (!value) return Promise.resolve();

    try {
      const response = await axios.get(
        `https://techsolutionsservice.ustdev.com/signup/userIdCheck/${value}`,{headers:{
          Authorization:`Bearer ${sessionStorage.getItem("authToken")}`
        }}
      );
      if (response.data) {
        return Promise.reject(new Error(response.data));
      }
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(
        new Error(error.response?.data || "Error validating userId")
      );
    }
  };

  const checkDuplicateUserId = (rule, value, index) => {
    if (!value) return Promise.resolve();

    const formValues = form.getFieldValue("invites") || [];
    const duplicates = formValues.filter(
      (item, i) => i !== index && item && item.userId === value
    );

    if (duplicates.length > 0) {
      return Promise.reject(new Error("User ID already entered"));
    }

    return Promise.resolve();
  };

  const checkDuplicateEmail = (rule, value, index) => {
    if (!value) return Promise.resolve();

    const formValues = form.getFieldValue("invites") || [];
    const duplicates = formValues.filter(
      (item, i) => i !== index && item && item.emailId === value
    );

    if (duplicates.length > 0) {
      return Promise.reject(new Error("Email already entered"));
    }

    return Promise.resolve();
  };

  const checkLastRowComplete = () => {
    const formValues = form.getFieldValue("invites") || [];
    if (formValues.length === 0) return true;

    const lastRow = formValues[formValues.length - 1];
    if (!lastRow) return false;

    return lastRow.userId && lastRow.emailId && lastRow.role;
  };

  const addNewRow = (add) => {
    if (checkLastRowComplete()) {
      add();
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = contentRef.current.scrollHeight;
        }
      }, 50);
    } else {
      message.warning(
        "Please fill all fields in the current row before adding a new one"
      );
    }
  };

  const handleSubmit = async (values) => {
    try {
      const payLoad = values.invites.map((invite) => ({
        userId: invite.userId,
        emailId: invite.emailId,
        role: invite.role,
        supplierDetailId: supplierId,
      }));

      const response = await fetch(API_BASE_URL + "signup/invite", {
        method: "POST",
        headers: {
          Authorization:`Bearer ${sessionStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payLoad),
      });

      const result = await response.text();

      console.log("API Response:", result);

      if (response.ok) {
        if (result.includes("invited successfully")) {
          message.success(result);
        } else {
          message.warning(result);
        }
      } else {
        message.error("Failed to send invitations");
      }

      form.resetFields();
      onClose();
    } catch (error) {
      console.error("Failed to send invitations:", error);
      message.error("An unexpected error occurred while sending invitations.");
    }
  };

  const handleClearForm = () => {
    form.resetFields();
    form.setFieldsValue({
      invites: [{}],
    });
  };

  const handleClearFormModal = () => {
    Modal.confirm({
      title: "Are you sure you want to clear all the entries",
      onOk() {
        handleClearForm();
      },
    });
  };

  useEffect(() => {
    if (isVisible) {
      form.setFieldsValue({
        invites: [{}],
      });
    }
  }, [isVisible, form]);

  const scrollToFirstError = () => {
    const errorElements = document.querySelectorAll(
      ".ant-form-item-explain-error"
    );
    if (errorElements.length > 0) {
      const errorItem = errorElements[0].closest(".ant-form-item");
      if (errorItem && contentRef.current) {
        const errorPosition = errorItem.offsetTop;
        contentRef.current.scrollTop = errorPosition - 50;
      }
    }
  };

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      maskClosable={false}
      footer={null}
      destroyOnClose
      width={isMobile ? "95%" : 900}
      style={{
        borderRadius: "8px",
        top: isMobile ? "10px" : "25px",
        margin: isMobile ? "0 auto" : undefined,
        maxWidth: "100vw",
      }}
      bodyStyle={{
        padding: "0",
        maxHeight: "calc(100vh - 100px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="pb-2 border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="text-[#1F405A] text-[18px] md:text-[20px] font-bold text-center py-3">
          ADD USERS
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ invites: [{}] }}
        onFinishFailed={() => {
          scrollToFirstError();
        }}
      >
        <div
          ref={contentRef}
          style={{
            flexGrow: 1,
            maxHeight: isMobile ? "calc(100vh - 220px)" : "calc(100vh - 280px)",
            overflowY: "auto",
            padding: isMobile ? "15px 10px 0 10px" : "20px 20px 0 20px",
          }}
        >
          {!isMobile && (
            <div className="mb-2 hidden md:block">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-4 font-medium flex items-center">
                  <IdcardOutlined className="ml-1 pr-2" /> 
                  User ID
                  <span className="text-red-500 pl-1">*</span> 
                </div>
                <div className="col-span-4 font-medium flex items-center">
                  <MailOutlined className="ml-1 pr-2" /> 
                  Email
                  <span className="text-red-500 pl-1">*</span> 
                </div>
                <div className="col-span-3 font-medium flex items-center">
                  <TeamOutlined className="ml-1 pr-2" /> 
                  Role
                  <span className="text-red-500 pl-1">*</span> 
                </div>
              </div>
            </div>
          )}

          <Form.List name="invites">
            {(fields, { add, remove }) => (
              <div>
                {fields.map((field, index) => (
                  <React.Fragment key={field.key}>
                    {isMobile ? (
                      <div className="mb-5 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <div className="mb-2">
                          <div className="font-medium flex items-center text-sm mb-1">
                            <IdcardOutlined className="mr-1" /> User ID
                          </div>
                          <Form.Item
                            {...field}
                            name={[field.name, "userId"]}
                            rules={[
                              {
                                required: true,
                                message: "Please enter user ID",
                              },
                              VALIDATION_RULES.userIdmax10,
                              {
                                validator: async (_, value) => {
                                  if (!value) return Promise.resolve();
                                  try {
                                    await validateUserIdAsync(_, value.trim());
                                    return Promise.resolve();
                                  } catch (error) {
                                    return Promise.reject(
                                      new Error(error.message)
                                    );
                                  }
                                },
                              },
                              {
                                validator: async (_, value) =>
                                  checkDuplicateUserId(_, value, index),
                              },
                            ]}
                            validateTrigger={["onChange", "onBlur"]}
                            className="mb-3"
                          >
                            <Input
                              placeholder="Enter user ID"
                              className="w-full rounded-md py-2 px-3 border border-gray-300"
                              onKeyPress={(e) => {
                                const specialChars =
                                  /[~`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/? ]/;
                                if (specialChars.test(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                            />
                          </Form.Item>
                        </div>

                        <div className="mb-2">
                          <div className="font-medium flex items-center text-sm mb-1">
                            <MailOutlined className="mr-1" /> Email
                          </div>
                          <Form.Item
                            {...field}
                            name={[field.name, "emailId"]}
                            rules={[
                              VALIDATION_RULES.emailRequired,
                              VALIDATION_RULES.max100,
                              {
                                validator: async (_, value) => {
                                  if (!value) return Promise.resolve();
                                  const emailRegex =
                                    /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,20}$/;
                                  if (!emailRegex.test(value)) {
                                    return Promise.reject(
                                      new Error("Invalid email address.")
                                    );
                                  }
                                  try {
                                    await validateEmailAsync(_, value.trim());
                                    return Promise.resolve();
                                  } catch (error) {
                                    return Promise.reject(
                                      new Error(error.message)
                                    );
                                  }
                                },
                              },
                              {
                                validator: async (_, value) =>
                                  checkDuplicateEmail(_, value, index),
                              },
                            ]}
                            validateTrigger={["onChange", "onBlur"]}
                            className="mb-3"
                          >
                            <Input
                              placeholder="Enter email address"
                              className="w-full rounded-md py-2 px-3 border border-gray-300"
                            />
                          </Form.Item>
                        </div>

                        <div className="mb-2">
                          <div className="font-medium flex items-center text-sm mb-1">
                            <TeamOutlined className="mr-1" /> Role
                          </div>
                          <Form.Item
                            {...field}
                            name={[field.name, "role"]}
                            rules={[
                              {
                                required: true,
                                message: "Please select a role",
                              },
                            ]}
                            className="mb-2"
                          >
                            <Select
                              placeholder="Select Role"
                              className="w-full h-[39.33px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              dropdownStyle={{ borderRadius: "4px" }}
                            >
                              <Option value="Supplier User">
                                Supplier User
                              </Option>
                              <Option value="Supplier Admin">
                                Supplier Admin
                              </Option>
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="flex justify-end mt-2">
                          {fields.length > 1 && (
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(field.name)}
                              className="mr-2"
                            >
                              Remove
                            </Button>
                          )}
                          {index === fields.length - 1 && (
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={() => addNewRow(add)}
                              style={{ backgroundColor: "#1F405A" }}
                            >
                              Add
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-12 gap-4 mb-4 items-start">
                        <div className="col-span-4">
                          <div style={{ minHeight: "35px" }}>
                            <Form.Item
                              {...field}
                              name={[field.name, "userId"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter user ID",
                                },
                                VALIDATION_RULES.userIdmax10,
                                {
                                  validator: async (_, value) => {
                                    if (!value) return Promise.resolve();
                                    try {
                                      await validateUserIdAsync(
                                        _,
                                        value.trim()
                                      );
                                      return Promise.resolve();
                                    } catch (error) {
                                      return Promise.reject(
                                        new Error(error.message)
                                      );
                                    }
                                  },
                                },
                                {
                                  validator: async (_, value) =>
                                    checkDuplicateUserId(_, value, index),
                                },
                              ]}
                              validateTrigger={["onChange", "onBlur"]}
                              className="mb-0"
                            >
                              <Input
                                placeholder="Enter user ID"
                                className="w-full rounded-md py-2 px-3 border border-gray-300"
                                onKeyPress={(e) => {
                                  const specialChars =
                                    /[~`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/? ]/;
                                  if (specialChars.test(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                              />
                            </Form.Item>
                          </div>
                        </div>
                        <div className="col-span-4">
                          <div style={{ minHeight: "35px" }}>
                            <Form.Item
                              {...field}
                              name={[field.name, "emailId"]}
                              rules={[
                                VALIDATION_RULES.emailRequired,
                                VALIDATION_RULES.max100,
                                {
                                  validator: async (_, value) => {
                                    if (!value) return Promise.resolve();
                                    const emailRegex =
                                      /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,20}$/;
                                    if (!emailRegex.test(value)) {
                                      return Promise.reject(
                                        new Error("Invalid email address.")
                                      );
                                    }
                                    try {
                                      await validateEmailAsync(_, value.trim());
                                      return Promise.resolve();
                                    } catch (error) {
                                      return Promise.reject(
                                        new Error(error.message)
                                      );
                                    }
                                  },
                                },
                                {
                                  validator: async (_, value) =>
                                    checkDuplicateEmail(_, value, index),
                                },
                              ]}
                              validateTrigger={["onChange", "onBlur"]}
                              className="mb-0"
                            >
                              <Input
                                placeholder="Enter email address"
                                className="w-full rounded-md py-2 px-3 border border-gray-300"
                              />
                            </Form.Item>
                          </div>
                        </div>
                        <div className="col-span-3">
                          <div style={{ minHeight: "35px" }}>
                            <Form.Item
                              {...field}
                              name={[field.name, "role"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please select a role",
                                },
                              ]}
                              className="mb-0"
                            >
                              <Select
                                placeholder="Select Role"
                                className="w-full h-[39.33px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                dropdownStyle={{ borderRadius: "4px" }}
                              >
                                <Option value="Supplier User">
                                  Supplier User
                                </Option>
                                <Option value="Supplier Admin">
                                  Supplier Admin
                                </Option>
                              </Select>
                            </Form.Item>
                          </div>
                        </div>
                        <div className="col-span-1 flex justify-center items-start">
                          <Space>
                            {fields.length > 1 && (
                              <Button
                                type="text"
                                icon={
                                  <DeleteOutlined
                                    style={{ color: "#ff4d4f" }}
                                  />
                                }
                                onClick={() => remove(field.name)}
                                className="flex items-center justify-center h-8 w-8"
                                style={{ border: "none" }}
                              />
                            )}
                            {index === fields.length - 1 && (
                              <Button
                                type="text"
                                icon={
                                  <PlusOutlined className="text-[#1F405A] font-bold" />
                                }
                                onClick={() => addNewRow(add)}
                                className="flex items-center justify-center h-8 w-8"
                                style={{ border: "none" }}
                              />
                            )}
                          </Space>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </Form.List>
          <div style={{ height: "20px" }}></div>
        </div>

        <div className="p-3 md:p-5 border-t border-gray-200 bg-white sticky bottom-0 z-10 mt-auto">
          <div className="flex justify-end">
            <Space
              direction={isMobile ? "vertical" : "horizontal"}
              style={{ width: isMobile ? "100%" : "auto" }}
            >
              <Button
                onClick={handleClearFormModal}
                className={`px-4 md:px-6 py-1 md:py-2 rounded-md ${
                  isMobile ? "w-full" : ""
                }`}
                style={{ borderColor: "#d9d9d9" }}
              >
                Clear
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                className={`px-4 md:px-6 py-1 md:py-2 rounded-md ${
                  isMobile ? "w-full" : ""
                }`}
                style={{ backgroundColor: "#1F405A", color: "#fff" }}
              >
                Add Users
              </Button>
            </Space>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default InviteForm;
